import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, Plus, Search, Trash2, Edit2, Upload, Camera,
  FileText, FolderOpen, Calendar, Euro, TrendingUp, TrendingDown,
  Download, Send, X, Save, Eye, ChevronDown, Filter, Building,
  ShoppingCart, Building2, Store, Hammer, Truck, Wifi, Shield,
  Package, ArrowLeft, BarChart3, PieChart, RefreshCw, Mail, CheckCircle,
  Sparkles, Loader2, Zap, Check, Archive, History, FileArchive, Copy, AlertTriangle, Info, HelpCircle,
  Folders
} from 'lucide-react';
import DocumentsExplorer from '../components/DocumentsExplorer';

// Utiliser l'URL de l'environnement OU l'origine actuelle (pour la production)
const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Composant InfoTooltip pour expliquer les calculs
const InfoTooltip = ({ title, description, formula, light = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
          light 
            ? 'bg-slate-200 hover:bg-slate-300 text-slate-600 hover:text-slate-800'
            : 'bg-white/20 hover:bg-white/30 text-white/70 hover:text-white'
        }`}
        title="Cliquez pour plus d'infos"
      >
        <Info className="w-3 h-3" />
      </button>
      {isOpen && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl">
          <div className="font-semibold text-sm mb-1 text-blue-300">{title}</div>
          <p className="text-slate-300 mb-2">{description}</p>
          {formula && (
            <div className="bg-slate-800 rounded px-2 py-1 font-mono text-[10px] text-green-300">
              📊 {formula}
            </div>
          )}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-900"></div>
        </div>
      )}
    </div>
  );
};

// Composant PageHelp pour expliquer le fonctionnement d'une page
const PageHelp = ({ title, description, items }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-blue-400" />
          <span className="font-semibold text-blue-300">{title}</span>
        </div>
        <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
          {isExpanded ? 'Masquer' : 'Comment ça marche ?'}
        </Button>
      </div>
      {isExpanded && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-white/70">{description}</p>
          {items && (
            <ul className="space-y-2">
              {items.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-blue-400 font-bold">•</span>
                  <span className="text-white/60">{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// Mapping des icônes
const ICON_MAP = {
  ShoppingCart, Building, Store, Hammer, Truck, Wifi, Shield,
  FileText, FolderOpen, Package
};

export default function AccountingPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // États
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [monthlyStats, setMonthlyStats] = useState(null);
  
  // Filtres de période avancés
  const [periodType, setPeriodType] = useState('month'); // month, quarter, semester, year, custom
  const [selectedQuarter, setSelectedQuarter] = useState(`${new Date().getFullYear()}-Q${Math.ceil((new Date().getMonth() + 1) / 3)}`);
  const [selectedSemester, setSelectedSemester] = useState(`${new Date().getFullYear()}-S${new Date().getMonth() < 6 ? 1 : 2}`);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [customStartDate, setCustomStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10));
  const [customEndDate, setCustomEndDate] = useState(new Date().toISOString().slice(0, 10));
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [savingDocument, setSavingDocument] = useState(false);
  
  // Sélection et export documents
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [showArchivesModal, setShowArchivesModal] = useState(false);
  const [archives, setArchives] = useState([]);
  const [loadingArchives, setLoadingArchives] = useState(false);
  const [accountantEmail, setAccountantEmail] = useState('contrat@phonelab69.fr');
  const [preparingZip, setPreparingZip] = useState(false);
  
  // IA Analysis
  const [analyzingDocument, setAnalyzingDocument] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  const [autoAnalyze, setAutoAnalyze] = useState(true); // Analyse auto activée par défaut
  
  // Suivi documents déjà envoyés
  const [alreadySentDocs, setAlreadySentDocs] = useState([]);
  const [checkingSentStatus, setCheckingSentStatus] = useState(false);
  
  // Doublons et suppression multiple
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [deletingDocs, setDeletingDocs] = useState(false);
  
  // Import multiple
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkImportProgress, setBulkImportProgress] = useState({ current: 0, total: 0, results: [] });
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  
  // Import ZIP
  const [showZipImportModal, setShowZipImportModal] = useState(false);
  const [zipImportProgress, setZipImportProgress] = useState(null);
  const [isImportingZip, setIsImportingZip] = useState(false);
  
  // Détection problèmes et réanalyse
  const [showIssuesModal, setShowIssuesModal] = useState(false);
  const [documentIssues, setDocumentIssues] = useState(null);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [reanalyzingDocs, setReanalyzingDocs] = useState(new Set());
  
  // Réanalyse globale
  const [showReanalyzeModal, setShowReanalyzeModal] = useState(false);
  const [reanalyzeStatus, setReanalyzeStatus] = useState(null);
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [reanalyzeProgress, setReanalyzeProgress] = useState(null);
  
  // Onglet actif: 'import' (vue classique) ou 'explorer' (explorateur de dossiers) ou 'non-envoyes' (à envoyer)
  const [activeTab, setActiveTab] = useState('import');
  
  // Onglet Documents Non Envoyés - Liste temporaire des docs à exclure de l'envoi
  const [excludedFromSend, setExcludedFromSend] = useState(new Set()); // IDs des docs exclus temporairement
  const [pendingDocsToSend, setPendingDocsToSend] = useState(new Set()); // IDs des docs à envoyer
  
  // Alerte date suspecte avant sauvegarde
  const [showDateAlertModal, setShowDateAlertModal] = useState(false);
  const [suspiciousDateDoc, setSuspiciousDateDoc] = useState(null); // Document avec date suspecte en attente
  
  // Drag & Drop pour import multiple
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);
  
  // Formulaire
  const [formData, setFormData] = useState({
    title: '',
    category_id: 'cat_achat',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    supplier: '',
    reference: '',
    notes: '',
    file_data: null,
    file_name: null,
    file_type: null
  });
  
  // Nouvelle catégorie
  const [newCategory, setNewCategory] = useState({ name: '', color: '#6366f1' });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, [token, periodType, selectedMonth, selectedQuarter, selectedSemester, selectedYear, customStartDate, customEndDate, selectedCategory]);

  // Construire les paramètres de période pour l'API
  const buildPeriodParams = () => {
    switch (periodType) {
      case 'month':
        return `period_type=month&month=${selectedMonth}`;
      case 'quarter':
        return `period_type=quarter&quarter=${selectedQuarter}`;
      case 'semester':
        return `period_type=semester&semester=${selectedSemester}`;
      case 'year':
        return `period_type=year&year=${selectedYear}`;
      case 'custom':
        return `period_type=custom&start_date=${customStartDate}&end_date=${customEndDate}`;
      default:
        return `period_type=month&month=${selectedMonth}`;
    }
  };

  // Obtenir les dates de début et fin pour le filtre documents
  const getDateRange = () => {
    const now = new Date();
    switch (periodType) {
      case 'month':
        return { start: `${selectedMonth}-01`, end: `${selectedMonth}-31` };
      case 'quarter': {
        const [year, q] = selectedQuarter.split('-Q');
        const qNum = parseInt(q);
        const startMonth = (qNum - 1) * 3 + 1;
        const endMonth = qNum * 3;
        return { 
          start: `${year}-${String(startMonth).padStart(2, '0')}-01`,
          end: `${year}-${String(endMonth).padStart(2, '0')}-31`
        };
      }
      case 'semester': {
        const [year, s] = selectedSemester.split('-S');
        const sNum = parseInt(s);
        return sNum === 1 
          ? { start: `${year}-01-01`, end: `${year}-06-30` }
          : { start: `${year}-07-01`, end: `${year}-12-31` };
      }
      case 'year':
        return { start: `${selectedYear}-01-01`, end: `${selectedYear}-12-31` };
      case 'custom':
        return { start: customStartDate, end: customEndDate };
      default:
        return { start: `${selectedMonth}-01`, end: `${selectedMonth}-31` };
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les catégories
      const catResponse = await axios.get(`${API}/api/accounting/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(catResponse.data);
      
      // Charger les documents selon la période
      const { start, end } = getDateRange();
      let url = `${API}/api/accounting/documents?month=${periodType === 'month' ? selectedMonth : ''}`;
      if (selectedCategory !== 'all') {
        url += `&category_id=${selectedCategory}`;
      }
      
      // Pour les périodes autres que mois, on charge tous les documents puis on filtre côté client
      // (ou on peut modifier l'API pour supporter les plages de dates)
      const docResponse = await axios.get(url.replace(`month=${periodType === 'month' ? selectedMonth : ''}`, `month=`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let allDocs = docResponse.data.items || docResponse.data;
      // Filtrer par dates si pas en mode mois
      if (periodType !== 'month') {
        allDocs = allDocs.filter(doc => doc.date >= start && doc.date <= end);
      } else {
        allDocs = allDocs.filter(doc => doc.date && doc.date.startsWith(selectedMonth));
      }
      setDocuments(allDocs);
      
      // Charger les stats avec le nouvel endpoint
      const statsResponse = await axios.get(`${API}/api/accounting/stats/period?${buildPeriodParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthlyStats(statsResponse.data);
      
      // Réinitialiser la sélection lors du changement de période
      setSelectedDocs(new Set());
      
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Charger les archives d'envoi
  const loadArchives = async () => {
    setLoadingArchives(true);
    try {
      const response = await axios.get(`${API}/api/ai-document/export/archives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchives(response.data);
    } catch (error) {
      console.error('Erreur chargement archives:', error);
      toast.error('Erreur lors du chargement des archives');
    } finally {
      setLoadingArchives(false);
    }
  };

  // Toggle sélection d'un document
  const toggleDocSelection = (docId) => {
    const newSelection = new Set(selectedDocs);
    if (newSelection.has(docId)) {
      newSelection.delete(docId);
    } else {
      newSelection.add(docId);
    }
    setSelectedDocs(newSelection);
  };

  // Sélectionner/Désélectionner tous les documents
  const toggleSelectAll = () => {
    const docsWithFiles = filteredDocuments.filter(d => d.has_file);
    if (selectedDocs.size === docsWithFiles.length) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(docsWithFiles.map(d => d.id)));
    }
  };

  // Vérifier le statut d'envoi des documents sélectionnés
  const checkSentStatus = async (docIds) => {
    if (!docIds || docIds.length === 0) {
      setAlreadySentDocs([]);
      return;
    }
    
    setCheckingSentStatus(true);
    try {
      const response = await axios.get(`${API}/api/ai-document/documents/sent-status`, {
        params: { document_ids: docIds.join(',') },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const sentDocs = response.data.documents.filter(d => d.sent_at);
      setAlreadySentDocs(sentDocs);
    } catch (error) {
      console.error('Erreur vérification statut:', error);
      setAlreadySentDocs([]);
    } finally {
      setCheckingSentStatus(false);
    }
  };

  // Ouvrir modal export avec vérification
  const openExportModal = () => {
    setShowExportModal(true);
    checkSentStatus(Array.from(selectedDocs));
  };

  // Charger les doublons
  const loadDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const response = await axios.get(`${API}/api/accounting/documents/duplicates`, {
        params: { month: selectedMonth },
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicateGroups(response.data.duplicates || []);
      setShowDuplicatesModal(true);
    } catch (error) {
      console.error('Erreur chargement doublons:', error);
      toast.error('Erreur lors de la détection des doublons');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  // Supprimer des documents en masse
  const bulkDeleteDocuments = async (docIds) => {
    if (!docIds || docIds.length === 0) return;
    
    setDeletingDocs(true);
    try {
      const response = await axios.delete(`${API}/api/accounting/documents/bulk-delete`, {
        data: docIds,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.deleted_count} document(s) supprimé(s)`);
      
      // Recharger les données et fermer le modal si plus de doublons
      loadData();
      setSelectedDocs(new Set());
      
      // Recharger les doublons si le modal est ouvert
      if (showDuplicatesModal) {
        const dupResponse = await axios.get(`${API}/api/accounting/documents/duplicates`, {
          params: { month: selectedMonth },
          headers: { Authorization: `Bearer ${token}` }
        });
        setDuplicateGroups(dupResponse.data.duplicates || []);
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeletingDocs(false);
    }
  };

  // Supprimer les documents sélectionnés
  const deleteSelectedDocuments = async () => {
    if (selectedDocs.size === 0) {
      toast.error('Sélectionnez au moins un document');
      return;
    }
    
    if (!window.confirm(`Supprimer ${selectedDocs.size} document(s) ? Cette action est irréversible.`)) {
      return;
    }
    
    await bulkDeleteDocuments(Array.from(selectedDocs));
  };

  // Télécharger le ZIP localement
  const downloadZip = async () => {
    if (selectedDocs.size === 0) {
      toast.error('Sélectionnez au moins un document');
      return;
    }
    
    setPreparingZip(true);
    try {
      const response = await axios.post(`${API}/api/ai-document/export/prepare-zip`, {
        document_ids: Array.from(selectedDocs),
        month: selectedMonth
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000 // 2 minutes timeout pour les gros fichiers
      });
      
      if (!response.data || !response.data.zip_data) {
        toast.error('Erreur: données ZIP invalides');
        return;
      }
      
      // Créer le lien de téléchargement
      const byteCharacters = atob(response.data.zip_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.zip_filename || `Comptabilite_${selectedMonth}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`ZIP téléchargé (${response.data.document_count} fichiers)`);
      setShowExportModal(false);
    } catch (error) {
      console.error('Erreur préparation ZIP:', error);
      const detail = error.response?.data?.detail || '';
      if (detail.includes('non trouvés') || detail.includes('not found')) {
        toast.error('Aucun document avec fichier attaché trouvé');
      } else if (detail.includes('Aucun document')) {
        toast.error('Sélectionnez au moins un document avec fichier');
      } else if (detail.includes('Aucun fichier valide')) {
        toast.error('Les documents sélectionnés n\'ont pas de fichiers valides');
      } else {
        toast.error(detail || 'Erreur lors de la création du ZIP');
      }
    } finally {
      setPreparingZip(false);
    }
  };

  // Import ZIP
  const importZipFile = async (file) => {
    if (!file) return;
    
    setIsImportingZip(true);
    setZipImportProgress({ status: 'reading', message: 'Lecture du fichier ZIP...' });
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        setZipImportProgress({ status: 'analyzing', message: 'Analyse des fichiers en cours...' });
        
        try {
          const response = await axios.post(`${API}/api/ai-document/import-zip`, {
            file_data: base64,
            file_name: file.name
          }, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 300000 // 5 minutes
          });
          
          setZipImportProgress({
            status: 'done',
            message: `Import terminé: ${response.data.imported}/${response.data.total_files} fichiers`,
            results: response.data.results,
            errors: response.data.errors
          });
          
          toast.success(`${response.data.imported} document(s) importé(s) depuis le ZIP`);
          loadData();
        } catch (error) {
          console.error('Erreur import ZIP:', error);
          setZipImportProgress({
            status: 'error',
            message: error.response?.data?.detail || 'Erreur lors de l\'import'
          });
          toast.error('Erreur lors de l\'import du ZIP');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erreur lecture ZIP:', error);
      setZipImportProgress({ status: 'error', message: 'Erreur lecture fichier' });
    } finally {
      setIsImportingZip(false);
    }
  };

  // Détecter les documents avec problèmes
  const detectDocumentIssues = async () => {
    setLoadingIssues(true);
    try {
      const response = await axios.get(`${API}/api/ai-document/detect-issues?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentIssues(response.data);
    } catch (error) {
      console.error('Erreur détection problèmes:', error);
      toast.error('Erreur lors de la détection des problèmes');
    } finally {
      setLoadingIssues(false);
    }
  };

  // Réanalyser un document
  const reanalyzeDocument = async (docId) => {
    setReanalyzingDocs(prev => new Set([...prev, docId]));
    try {
      const response = await axios.post(`${API}/api/ai-document/reanalyze/${docId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Document réanalysé: ${response.data.new_data.title} - ${response.data.new_data.amount}€`);
        loadData();
        detectDocumentIssues(); // Rafraîchir la liste des problèmes
      }
    } catch (error) {
      console.error('Erreur réanalyse:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réanalyse');
    } finally {
      setReanalyzingDocs(prev => {
        const newSet = new Set(prev);
        newSet.delete(docId);
        return newSet;
      });
    }
  };

  // Charger le statut de réanalyse
  const loadReanalyzeStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/ai-document/reanalyze-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReanalyzeStatus(response.data);
    } catch (error) {
      console.error('Erreur chargement statut réanalyse:', error);
    }
  };

  // Lancer la réanalyse globale
  const startGlobalReanalysis = async (month = null, force = false) => {
    setIsReanalyzing(true);
    setReanalyzeProgress({ status: 'starting', message: 'Démarrage de la réanalyse...' });
    
    try {
      const response = await axios.post(`${API}/api/ai-document/reanalyze-all`, 
        { month, force },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReanalyzeProgress({
        status: 'complete',
        message: response.data.message,
        ...response.data
      });
      
      if (response.data.updated > 0) {
        toast.success(`✅ ${response.data.updated} document(s) mis à jour avec succès!`);
        loadData();
        loadReanalyzeStatus();
      } else if (response.data.analyzed > 0) {
        toast.info('Tous les documents étaient déjà corrects');
      } else {
        toast.info(response.data.message || 'Aucun document à réanalyser');
      }
    } catch (error) {
      console.error('Erreur réanalyse globale:', error);
      setReanalyzeProgress({
        status: 'error',
        message: error.response?.data?.detail || 'Erreur lors de la réanalyse'
      });
      toast.error('Erreur lors de la réanalyse');
    } finally {
      setIsReanalyzing(false);
    }
  };

  // Réanalyser tous les documents avec problèmes
  const reanalyzeAllIssues = async () => {
    if (!documentIssues?.issues?.length) return;
    
    const docsWithFiles = documentIssues.issues.filter(i => i.has_file);
    if (docsWithFiles.length === 0) {
      toast.error('Aucun document avec fichier à réanalyser');
      return;
    }
    
    const confirm = window.confirm(`Réanalyser ${docsWithFiles.length} document(s) avec l'IA ?`);
    if (!confirm) return;
    
    try {
      const response = await axios.post(`${API}/api/ai-document/reanalyze-batch`, 
        docsWithFiles.map(d => d.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.reanalyzed} document(s) réanalysé(s)`);
      loadData();
      detectDocumentIssues();
    } catch (error) {
      console.error('Erreur réanalyse batch:', error);
      toast.error('Erreur lors de la réanalyse');
    }
  };

  // Envoyer par email
  const sendToAccountant = async () => {
    if (selectedDocs.size === 0) {
      toast.error('Sélectionnez au moins un document');
      return;
    }
    
    if (!accountantEmail) {
      toast.error('Veuillez saisir l\'email du comptable');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/api/ai-document/export/send-email`, {
        document_ids: Array.from(selectedDocs),
        month: selectedMonth,
        recipient_email: accountantEmail,
        message: emailMessage || null,
        compress: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`📧 ${response.data.document_count} document(s) envoyé(s) à ${accountantEmail}`);
      setShowExportModal(false);
      setEmailMessage('');
      setSelectedDocs(new Set());
      setAlreadySentDocs([]);
      
      // Recharger les documents et archives pour mettre à jour les badges
      loadData();
      loadArchives();
      
    } catch (error) {
      console.error('Erreur envoi:', error);
      const errorDetail = error.response?.data?.detail || '';
      
      // Vérifier si c'est une erreur Resend de domaine non vérifié
      if (errorDetail.includes('verify a domain') || errorDetail.includes('testing emails')) {
        toast.error(
          <div>
            <strong>Domaine email non vérifié</strong>
            <p className="text-sm mt-1">Pour envoyer à d'autres adresses, configurez votre domaine sur resend.com</p>
            <p className="text-sm mt-1">💡 Utilisez le bouton "ZIP" pour télécharger localement</p>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(errorDetail || 'Erreur lors de l\'envoi');
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10 Mo)');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData({
          ...formData,
          file_data: event.target.result.split(',')[1],
          file_name: file.name,
          file_type: file.type
        });
        toast.success(`Fichier "${file.name}" ajouté`);
      };
      reader.readAsDataURL(file);
    }
  };

  // Import multiple - Sélection des fichiers (y compris dossiers)
  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Extensions supportées pour l'analyse IA
    const supportedExtensions = [
      '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff',
      '.xlsx', '.xls', '.csv', '.doc', '.docx', '.txt', '.rtf',
      '.heic', '.heif'
    ];
    
    const validFiles = files.filter(file => {
      // Vérifier la taille (max 15 Mo)
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`${file.name} trop volumineux (max 15 Mo)`);
        return false;
      }
      
      // Vérifier l'extension
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!supportedExtensions.includes(ext)) {
        toast.warning(`${file.name} - format non supporté pour l'analyse IA`);
        return false;
      }
      
      return true;
    });
    
    if (validFiles.length > 0) {
      // Enrichir avec le chemin relatif si import dossier
      const enrichedFiles = validFiles.map(file => {
        file.relativePath = file.webkitRelativePath || file.name;
        return file;
      });
      
      setBulkFiles(enrichedFiles);
      setShowBulkImportModal(true);
      setBulkImportProgress({ current: 0, total: enrichedFiles.length, results: [] });
      
      toast.info(`${enrichedFiles.length} fichier(s) prêt(s) à importer`);
    } else {
      toast.error('Aucun fichier valide sélectionné');
    }
    
    // Reset input pour permettre de resélectionner les mêmes fichiers
    e.target.value = '';
  };

  // Drag & Drop handlers pour import de dossiers multiples
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Vérifier si on quitte vraiment la zone
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const items = e.dataTransfer.items;
    const allFiles = [];

    // Extensions supportées
    const supportedExtensions = [
      '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff',
      '.xlsx', '.xls', '.csv', '.doc', '.docx', '.txt', '.rtf', '.heic', '.heif'
    ];

    // Fonction récursive pour lire les dossiers
    const readDirectory = async (entry, path = '') => {
      if (entry.isFile) {
        return new Promise((resolve) => {
          entry.file((file) => {
            // Ajouter le chemin relatif
            file.relativePath = path + file.name;
            
            // Vérifier l'extension
            const ext = '.' + file.name.split('.').pop().toLowerCase();
            if (supportedExtensions.includes(ext) && file.size <= 15 * 1024 * 1024) {
              resolve([file]);
            } else {
              resolve([]);
            }
          }, () => resolve([]));
        });
      } else if (entry.isDirectory) {
        const dirReader = entry.createReader();
        const entries = await new Promise((resolve) => {
          dirReader.readEntries(resolve, () => resolve([]));
        });
        
        const filesPromises = entries.map(e => 
          readDirectory(e, path + entry.name + '/')
        );
        const nestedFiles = await Promise.all(filesPromises);
        return nestedFiles.flat();
      }
      return [];
    };

    // Traiter tous les items déposés
    const promises = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.webkitGetAsEntry) {
        const entry = item.webkitGetAsEntry();
        if (entry) {
          promises.push(readDirectory(entry));
        }
      } else if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          const ext = '.' + file.name.split('.').pop().toLowerCase();
          if (supportedExtensions.includes(ext) && file.size <= 15 * 1024 * 1024) {
            file.relativePath = file.name;
            promises.push(Promise.resolve([file]));
          }
        }
      }
    }

    const results = await Promise.all(promises);
    const files = results.flat();

    if (files.length > 0) {
      setBulkFiles(files);
      setShowBulkImportModal(true);
      setBulkImportProgress({ current: 0, total: files.length, results: [] });
      toast.info(`${files.length} fichier(s) prêt(s) à importer`);
    } else {
      toast.error('Aucun fichier valide trouvé dans la sélection');
    }
  };

  // Import multiple - Analyser et importer tous les fichiers
  const processBulkImport = async () => {
    if (bulkFiles.length === 0) return;
    
    setIsBulkImporting(true);
    const results = [];
    
    for (let i = 0; i < bulkFiles.length; i++) {
      const file = bulkFiles[i];
      setBulkImportProgress(prev => ({ ...prev, current: i + 1 }));
      
      try {
        // Lire le fichier en base64
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        // Analyser avec l'IA
        const analyzeResponse = await axios.post(`${API}/api/ai-document/analyze`, {
          file_data: fileData,
          file_type: file.type,
          file_name: file.name
        }, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000
        });
        
        if (analyzeResponse.data.success) {
          const extracted = analyzeResponse.data.extracted_data;
          
          // VALIDATION DE DATE - Détecter les dates suspectes
          let documentDate = extracted.date;
          const today = new Date();
          const todayStr = today.toISOString().slice(0, 10);
          let dateWasCorrected = false;
          let originalExtractedDate = extracted.date;
          
          if (documentDate) {
            const extractedDate = new Date(documentDate);
            const oneYearAgo = new Date(today);
            oneYearAgo.setFullYear(today.getFullYear() - 1);
            const oneMonthAhead = new Date(today);
            oneMonthAhead.setMonth(today.getMonth() + 1);
            
            // Si date extraite est > 1 an dans le passé ou > 1 mois dans le futur
            if (extractedDate < oneYearAgo || extractedDate > oneMonthAhead) {
              console.warn(`⚠️ Date suspecte détectée: ${documentDate} pour ${file.name}`);
              dateWasCorrected = true;
              documentDate = todayStr;
              
              // Afficher un toast d'alerte pour informer l'utilisateur
              toast.warning(
                <div>
                  <strong>⚠️ Date suspecte corrigée</strong>
                  <p className="text-sm mt-1">{file.name}</p>
                  <p className="text-xs opacity-80">Date IA: {originalExtractedDate} → {todayStr}</p>
                </div>,
                { duration: 5000 }
              );
            }
          } else {
            documentDate = todayStr;
          }
          
          // Créer le document automatiquement
          const docData = {
            title: extracted.fournisseur ? `${extracted.type_document || 'Document'} - ${extracted.fournisseur}` : file.name.replace(/\.[^/.]+$/, ''),
            category_id: analyzeResponse.data.category_id || 'cat_achat',
            amount: Math.abs(extracted.montant || 0),
            date: documentDate,
            supplier: extracted.fournisseur || '',
            reference: extracted.reference || '',
            notes: extracted.description || '',
            file_data: fileData,
            file_name: file.name,
            file_type: file.type,
            suggested_category: analyzeResponse.data.suggested_category,
            sub_category: analyzeResponse.data.sub_category,
            ai_extracted_date: originalExtractedDate, // Garder la date originale extraite pour référence
            ai_confidence: analyzeResponse.data.confidence || 5,
            date_auto_corrected: dateWasCorrected // Marquer si la date a été corrigée
          };
          
          // IMPORTANT: Sauvegarder le document et vérifier le résultat
          try {
            const saveResponse = await axios.post(`${API}/api/accounting/documents`, docData, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log(`Document sauvegardé: ${file.name}`, saveResponse.data);
            results.push({ file: file.name, status: 'success', data: extracted });
          } catch (saveError) {
            console.error(`Erreur sauvegarde ${file.name}:`, saveError.response?.data || saveError);
            results.push({ 
              file: file.name, 
              status: 'error', 
              error: `Sauvegarde échouée: ${saveError.response?.data?.detail || saveError.message}` 
            });
          }
        } else {
          results.push({ file: file.name, status: 'error', error: 'Analyse échouée' });
        }
      } catch (error) {
        console.error(`Erreur pour ${file.name}:`, error);
        results.push({ 
          file: file.name, 
          status: 'error', 
          error: error.response?.data?.detail || error.message 
        });
      }
      
      setBulkImportProgress(prev => ({ ...prev, results: [...results] }));
    }
    
    setIsBulkImporting(false);
    
    // Résumé
    const successCount = results.filter(r => r.status === 'success').length;
    const errorCount = results.filter(r => r.status === 'error').length;
    // Compter les documents importés sans montant (IA n'a pas pu lire)
    const noAmountCount = results.filter(r => 
      r.status === 'success' && (!r.data?.montant || r.data.montant === 0)
    ).length;
    
    if (successCount > 0) {
      toast.success(`${successCount} document(s) importé(s) avec succès`);
      loadData();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} document(s) en erreur`);
    }
    if (noAmountCount > 0) {
      toast.warning(
        `⚠️ ${noAmountCount} document(s) sans montant détecté par l'IA. Modifiez-les pour saisir le montant manuellement.`,
        { duration: 8000 }
      );
    }
  };

  const openNewDocModal = () => {
    setEditingDoc(null);
    setFormData({
      title: '',
      category_id: 'cat_achat',
      amount: '',
      date: new Date().toISOString().slice(0, 10),
      supplier: '',
      reference: '',
      notes: '',
      file_data: null,
      file_name: null,
      file_type: null
    });
    setShowModal(true);
  };

  const openEditModal = (doc) => {
    setEditingDoc(doc);
    setFormData({
      title: doc.title,
      category_id: doc.category_id,
      amount: doc.amount.toString(),
      date: doc.date,
      supplier: doc.supplier || '',
      reference: doc.reference || '',
      notes: doc.notes || '',
      file_data: null,
      file_name: doc.file_name,
      file_type: doc.file_type
    });
    setShowModal(true);
  };

  const saveDocument = async () => {
    // Validation plus souple - le montant peut être 0 ou vide
    if (!formData.title) {
      toast.error('Veuillez saisir un titre pour le document');
      return;
    }
    if (!formData.date) {
      toast.error('Veuillez sélectionner une date');
      return;
    }

    setSavingDocument(true);
    
    try {
      // Utiliser le montant du formulaire ou celui de l'IA ou 0
      const finalAmount = parseFloat(formData.amount) || 0;
      
      const payload = {
        title: formData.title,
        category_id: formData.category_id || 'cat_achat',
        amount: finalAmount,
        date: formData.date,
        supplier: formData.supplier || '',
        reference: formData.reference || '',
        notes: formData.notes || '',
        file_data: formData.file_data || null,
        file_name: formData.file_name || null,
        file_type: formData.file_type || null,
        suggested_category: aiAnalysisResult?.suggested_category || null,
        sub_category: aiAnalysisResult?.sub_category || formData.supplier || null
      };
      
      console.log('=== SAVING DOCUMENT ===');
      console.log('Payload:', JSON.stringify(payload, null, 2));

      if (editingDoc) {
        const response = await axios.put(`${API}/api/accounting/documents/${editingDoc.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Update response:', response.data);
        toast.success('Document mis à jour !');
      } else {
        const response = await axios.post(`${API}/api/accounting/documents`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Create response:', response.data);
        toast.success('✅ Document enregistré avec succès !');
      }
      
      // Synchroniser le filtre de mois avec la date du document sauvegardé
      const docMonth = formData.date.slice(0, 7); // Format YYYY-MM
      if (docMonth !== selectedMonth) {
        setSelectedMonth(docMonth);
        toast.info(`📅 Affichage du mois ${docMonth} pour voir le document`);
      }
      
      // Fermer le modal et recharger
      setShowModal(false);
      setAiAnalysisResult(null);
      setFormData({
        title: '',
        category_id: 'cat_achat',
        amount: '',
        date: new Date().toISOString().slice(0, 10),
        supplier: '',
        reference: '',
        notes: '',
        file_data: null,
        file_name: null,
        file_type: null
      });
      
      // Recharger après un court délai pour laisser le state se mettre à jour
      setTimeout(() => loadData(), 100);
    } catch (error) {
      console.error('=== ERREUR SAUVEGARDE ===');
      console.error('Error:', error);
      console.error('Response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingDocument(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    
    try {
      await axios.delete(`${API}/api/accounting/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Visualiser un document (charge le fichier puis ouvre dans une nouvelle fenêtre)
  const viewDocument = async (doc) => {
    try {
      // Charger le document complet avec file_data depuis l'API
      const response = await axios.get(`${API}/api/accounting/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fullDoc = response.data;
      
      if (!fullDoc.file_data) {
        toast.error('Aucun fichier attaché à ce document');
        return;
      }
      
      // Créer un blob à partir des données base64
      const fileType = fullDoc.file_type || 'application/pdf';
      const byteCharacters = atob(fullDoc.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: fileType });
      
      // Ouvrir dans une nouvelle fenêtre
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Libérer l'URL après un délai
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Erreur visualisation:', error);
      toast.error('Erreur lors de l\'ouverture du document');
    }
  };

  const addCategory = async () => {
    if (!newCategory.name) {
      toast.error('Nom de catégorie requis');
      return;
    }
    
    try {
      await axios.post(`${API}/api/accounting/categories`, newCategory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Catégorie ajoutée');
      setNewCategory({ name: '', color: '#6366f1' });
      setShowCategoryModal(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.name : 'Autre';
  };

  const getCategoryColor = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.color : '#6b7280';
  };

  const getCategoryIcon = (catId) => {
    const cat = categories.find(c => c.id === catId);
    const iconName = cat?.icon || 'FileText';
    return ICON_MAP[iconName] || FileText;
  };

  const filteredDocuments = documents.filter(doc => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return doc.title.toLowerCase().includes(search) ||
             (doc.supplier && doc.supplier.toLowerCase().includes(search)) ||
             (doc.reference && doc.reference.toLowerCase().includes(search));
    }
    return true;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => {
              sessionStorage.setItem('reports_previous_path', '/accounting');
              navigate('/reports');
            }} variant="ghost" size="sm" className="text-white/70">
              <ArrowLeft className="w-4 h-4 mr-1" /> Rapports
            </Button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-indigo-400" />
              Comptabilité
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Bouton Historique des envois */}
            <Button 
              onClick={() => {
                loadArchives();
                setShowArchivesModal(true);
              }} 
              variant="ghost" 
              size="sm" 
              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-400/10"
            >
              <History className="w-4 h-4 mr-1" />
              Historique
            </Button>
            <Button 
              onClick={() => {
                sessionStorage.removeItem('reports_authenticated');
                navigate('/');
              }} 
              variant="ghost" 
              size="sm" 
              className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
            >
              <Home className="w-4 h-4 mr-1" />
              Quitter Rapports
            </Button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex items-center gap-2 mb-6 bg-white/5 backdrop-blur rounded-xl p-1 border border-white/10 overflow-x-auto">
          <button
            onClick={() => setActiveTab('import')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
              activeTab === 'import'
                ? 'bg-indigo-500 text-white shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            <Upload className="w-4 h-4" />
            Import & Analyse
          </button>
          <button
            onClick={() => setActiveTab('explorer')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
              activeTab === 'explorer'
                ? 'bg-indigo-500 text-white shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            <Folders className="w-4 h-4" />
            Explorateur
          </button>
          <button
            onClick={() => setActiveTab('non-envoyes')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap relative ${
              activeTab === 'non-envoyes'
                ? 'bg-amber-500 text-white shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            <Send className="w-4 h-4" />
            Non Envoyés
            {documents.filter(d => !d.sent_at && !excludedFromSend.has(d.id)).length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {documents.filter(d => !d.sent_at && !excludedFromSend.has(d.id)).length}
              </span>
            )}
          </button>
        </div>

        {/* Tab Content: Explorer */}
        {activeTab === 'explorer' ? (
          <DocumentsExplorer 
            token={token} 
            onViewDocument={viewDocument}
          />
        ) : activeTab === 'non-envoyes' ? (
          /* Onglet Non Envoyés */
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Send className="w-5 h-5 text-amber-400" />
                    Documents Non Envoyés
                  </h3>
                  <p className="text-white/50 text-sm mt-1">
                    Gérez les documents à envoyer au comptable. Décochez pour retirer temporairement de la liste d'envoi.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white/60 text-sm">
                    {documents.filter(d => !d.sent_at && !excludedFromSend.has(d.id)).length} document(s) à envoyer
                  </span>
                  {excludedFromSend.size > 0 && (
                    <Button
                      onClick={() => setExcludedFromSend(new Set())}
                      variant="outline"
                      size="sm"
                      className="border-amber-500/50 text-amber-400"
                    >
                      Réinitialiser exclusions ({excludedFromSend.size})
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Liste des documents non envoyés */}
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-white/10">
              {documents.filter(d => !d.sent_at).length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-400 opacity-50" />
                  <p className="text-white/60 text-lg">Tous les documents ont été envoyés</p>
                  <p className="text-white/40 text-sm mt-2">
                    Aucun document en attente d'envoi pour {formatMonth(selectedMonth)}
                  </p>
                </div>
              ) : (
                documents.filter(d => !d.sent_at).map(doc => {
                  const isExcluded = excludedFromSend.has(doc.id);
                  return (
                    <div 
                      key={doc.id}
                      className={`p-4 flex items-center gap-4 transition-all ${
                        isExcluded ? 'bg-red-500/10 opacity-60' : 'hover:bg-white/5'
                      }`}
                    >
                      {/* Checkbox pour inclure/exclure */}
                      <div className="flex-shrink-0">
                        <button
                          onClick={() => {
                            const newExcluded = new Set(excludedFromSend);
                            if (isExcluded) {
                              newExcluded.delete(doc.id);
                            } else {
                              newExcluded.add(doc.id);
                            }
                            setExcludedFromSend(newExcluded);
                          }}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                            isExcluded 
                              ? 'border-red-500 bg-red-500/20' 
                              : 'border-emerald-500 bg-emerald-500'
                          }`}
                        >
                          {!isExcluded && <CheckCircle className="w-4 h-4 text-white" />}
                          {isExcluded && <X className="w-4 h-4 text-red-400" />}
                        </button>
                      </div>
                      
                      {/* Icône fichier */}
                      <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-indigo-400" />
                      </div>
                      
                      {/* Infos document */}
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium truncate ${isExcluded ? 'text-white/50 line-through' : 'text-white'}`}>
                          {doc.title || 'Sans titre'}
                        </div>
                        <div className="text-sm text-white/50 flex items-center gap-3">
                          <span>{doc.date}</span>
                          {doc.supplier && <span>• {doc.supplier}</span>}
                          {doc.date_auto_corrected && (
                            <span className="text-amber-400 text-xs">⚠️ Date corrigée</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Montant */}
                      <div className="text-right flex-shrink-0">
                        <div className={`font-semibold ${isExcluded ? 'text-white/40' : 'text-emerald-400'}`}>
                          {doc.amount?.toFixed(2) || '0.00'} €
                        </div>
                        <div className="text-xs text-white/40">
                          {doc.category_name || 'Non catégorisé'}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {doc.file_data && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-white/60"
                            onClick={() => viewDocument(doc)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-white/60"
                          onClick={() => {
                            setEditingDoc(doc);
                            setFormData({
                              title: doc.title || '',
                              category_id: doc.category_id || 'cat_achat',
                              amount: doc.amount?.toString() || '',
                              date: doc.date || '',
                              supplier: doc.supplier || '',
                              reference: doc.reference || '',
                              notes: doc.notes || ''
                            });
                            setShowNewDocModal(true);
                          }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            {/* Footer avec résumé */}
            {documents.filter(d => !d.sent_at).length > 0 && (
              <div className="p-4 border-t border-white/10 bg-white/5">
                <div className="flex items-center justify-between">
                  <div className="text-white/60 text-sm">
                    <span className="text-emerald-400 font-medium">
                      {documents.filter(d => !d.sent_at && !excludedFromSend.has(d.id)).length}
                    </span> document(s) seront envoyés
                    {excludedFromSend.size > 0 && (
                      <span className="text-red-400 ml-2">
                        ({excludedFromSend.size} exclu(s) temporairement)
                      </span>
                    )}
                  </div>
                  <div className="text-white font-semibold">
                    Total: {documents
                      .filter(d => !d.sent_at && !excludedFromSend.has(d.id))
                      .reduce((sum, d) => sum + (d.amount || 0), 0)
                      .toFixed(2)} €
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
        <>
        {/* Stats Cards */}
        {monthlyStats && (
          <>
          {/* Guide d'utilisation */}
          <PageHelp
            title="📁 Page Comptabilité - Gestion des documents"
            description="Importez et analysez vos factures, relevés bancaires et documents comptables avec l'IA. Exportez-les par mois pour votre comptable."
            items={[
              "Importez des documents (photo, PDF, scan) - l'IA les analyse automatiquement",
              "Vérifiez et corrigez les informations extraites si nécessaire",
              "Cochez les documents à exporter et téléchargez le ZIP pour votre comptable",
              "Les documents exportés sont marqués avec une coche verte pour éviter les doublons"
            ]}
          />
          
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            {/* Encaissements = Chiffre d'affaires RÉEL */}
            <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="text-emerald-300 text-sm">Encaissements (CA)</div>
                <InfoTooltip 
                  title="Chiffre d'Affaires"
                  description="TOTAL des entrées d'argent : espèces + CB + chèques + virements. C'est l'argent qui ENTRE dans votre caisse."
                  formula="💵 Espèces + 💳 CB + 🏦 Chèques + Virements"
                />
              </div>
              <div className="text-2xl font-bold text-white">
                {formatCurrency(monthlyStats.cash_register?.total || monthlyStats.chiffre_affaires || monthlyStats.consolidated_revenue || 0)}
              </div>
              <div className="text-xs text-emerald-400 mt-1 flex flex-wrap gap-1">
                {monthlyStats.cash_register?.cash > 0 && <span>💵 {formatCurrency(monthlyStats.cash_register.cash)}</span>}
                {monthlyStats.cash_register?.card > 0 && <span>💳 {formatCurrency(monthlyStats.cash_register.card)}</span>}
              </div>
            </div>
            
            {/* Dépôts Banque */}
            {monthlyStats.bank_deposits && monthlyStats.bank_deposits.total > 0 && (
              <div className="bg-gradient-to-br from-indigo-500/20 to-indigo-600/10 border border-indigo-500/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-indigo-300 text-sm">Dépôts Banque</div>
                  <InfoTooltip 
                    title="Dépôts en Banque"
                    description="Espèces déposées sur votre compte bancaire professionnel."
                    formula="Σ Bordereaux de dépôt"
                  />
                </div>
                <div className="text-2xl font-bold text-white">{formatCurrency(monthlyStats.bank_deposits.total)}</div>
                <div className="text-xs text-indigo-400 mt-1">{monthlyStats.bank_deposits.count} dépôt(s)</div>
              </div>
            )}
            
            {/* Espèces en caisse */}
            {monthlyStats.cash_in_register !== undefined && (
              <div className={`bg-gradient-to-br ${monthlyStats.cash_in_register >= 0 ? 'from-teal-500/20 to-teal-600/10 border-teal-500/30' : 'from-red-500/20 to-red-600/10 border-red-500/30'} border rounded-xl p-4`}>
                <div className="flex items-center justify-between mb-1">
                  <div className={`${monthlyStats.cash_in_register >= 0 ? 'text-teal-300' : 'text-red-300'} text-sm`}>Espèces en caisse</div>
                  <InfoTooltip 
                    title="Espèces restantes"
                    description={monthlyStats.cash_in_register >= 0 
                      ? "Montant théorique en espèces dans votre caisse."
                      : "⚠️ Valeur négative = écart entre encaissements et dépôts/sorties."}
                    formula="Encaissé espèces - Déposé - Sorties"
                  />
                </div>
                <div className="text-2xl font-bold text-white">{formatCurrency(monthlyStats.cash_in_register)}</div>
                <div className="text-xs text-white/50 mt-1">Encaissé - Déposé</div>
              </div>
            )}
            
            {/* Dépenses = Factures importées (achats, charges, frais) */}
            <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 border border-red-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="text-red-300 text-sm">Dépenses (Achats)</div>
                <InfoTooltip 
                  title="Dépenses & Charges"
                  description="TOTAL des sorties d'argent : factures d'achat, marchandises, charges, frais, salaires, impôts. C'est l'argent qui SORT."
                  formula="Σ Documents importés (factures fournisseurs)"
                />
              </div>
              <div className="text-2xl font-bold text-white">{formatCurrency(monthlyStats.total_expenses + (monthlyStats.cash_expenses || 0))}</div>
              <div className="text-xs text-red-400 mt-1">{monthlyStats.document_count} documents importés</div>
            </div>
            
            {/* Avoirs (montants négatifs = crédits) */}
            {monthlyStats.total_avoirs !== undefined && monthlyStats.total_avoirs < 0 && (
              <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border border-cyan-500/30 rounded-xl p-4">
                <div className="text-cyan-300 text-sm mb-1">Avoirs reçus</div>
                <div className="text-2xl font-bold text-cyan-400">+{formatCurrency(Math.abs(monthlyStats.total_avoirs))}</div>
                <div className="text-xs text-cyan-400 mt-1">Crédits / Remboursements</div>
              </div>
            )}
            
            {/* Charges nettes */}
            <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="text-orange-300 text-sm">Charges nettes</div>
                <InfoTooltip 
                  title="Charges Nettes"
                  description="Dépenses totales moins les avoirs reçus."
                  formula="Dépenses + Sorties caisse - Avoirs"
                />
              </div>
              <div className="text-2xl font-bold text-white">{formatCurrency(monthlyStats.net_expenses || monthlyStats.total_expenses)}</div>
              <div className="text-xs text-orange-400 mt-1">Total à payer</div>
            </div>
            
            {/* Résultat = Marge = CA - Charges */}
            <div className={`bg-gradient-to-br ${monthlyStats.margin >= 0 ? 'from-green-500/20 to-green-600/10 border-green-500/30' : 'from-amber-500/20 to-amber-600/10 border-amber-500/30'} border rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-1">
                <div className={`${monthlyStats.margin >= 0 ? 'text-green-300' : 'text-amber-300'} text-sm`}>
                  {monthlyStats.margin >= 0 ? 'Bénéfice' : 'Perte'}
                </div>
                <InfoTooltip 
                  title="Résultat"
                  description="Différence entre les encaissements et les dépenses. Positif = bénéfice, Négatif = perte."
                  formula="Encaissements (CA) - Charges nettes"
                />
              </div>
              <div className="text-2xl font-bold text-white flex items-center gap-2">
                {monthlyStats.margin >= 0 ? <TrendingUp className="w-5 h-5 text-green-400" /> : <TrendingDown className="w-5 h-5 text-amber-400" />}
                {formatCurrency(monthlyStats.margin)}
              </div>
              <div className={`text-xs ${monthlyStats.margin >= 0 ? 'text-green-400' : 'text-amber-400'} mt-1`}>
                {monthlyStats.margin_percent}% du CA
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-4">
              <div className="text-purple-300 text-sm mb-2">Période d'affichage</div>
              
              {/* Sélecteur de type de période */}
              <div className="flex gap-1 mb-3 flex-wrap">
                {[
                  { value: 'month', label: 'Mois' },
                  { value: 'quarter', label: 'Trim.' },
                  { value: 'semester', label: 'Sem.' },
                  { value: 'year', label: 'Année' },
                  { value: 'custom', label: 'Custom' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setPeriodType(opt.value)}
                    className={`px-2 py-1 rounded text-xs transition-all ${
                      periodType === opt.value 
                        ? 'bg-purple-500 text-white' 
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              
              {/* Sélecteurs selon le type de période */}
              <div className="flex items-center gap-2">
                {periodType === 'month' && (
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm w-full"
                  />
                )}
                
                {periodType === 'quarter' && (
                  <select
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(e.target.value)}
                    className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm w-full"
                  >
                    {[...Array(4)].map((_, i) => {
                      const year = new Date().getFullYear();
                      return [-1, 0, 1].map(offset => (
                        <option key={`${year + offset}-Q${i + 1}`} value={`${year + offset}-Q${i + 1}`}>
                          T{i + 1} {year + offset}
                        </option>
                      ));
                    }).flat()}
                  </select>
                )}
                
                {periodType === 'semester' && (
                  <select
                    value={selectedSemester}
                    onChange={(e) => setSelectedSemester(e.target.value)}
                    className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm w-full"
                  >
                    {[-1, 0, 1].map(offset => {
                      const year = new Date().getFullYear() + offset;
                      return [1, 2].map(s => (
                        <option key={`${year}-S${s}`} value={`${year}-S${s}`}>
                          S{s} {year} ({s === 1 ? 'Jan-Juin' : 'Juil-Déc'})
                        </option>
                      ));
                    }).flat()}
                  </select>
                )}
                
                {periodType === 'year' && (
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm w-full"
                  >
                    {[-2, -1, 0, 1].map(offset => {
                      const year = new Date().getFullYear() + offset;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                )}
                
                {periodType === 'custom' && (
                  <div className="flex flex-col gap-1 w-full">
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs w-full"
                    />
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs w-full"
                    />
                  </div>
                )}
              </div>
              
              {/* Affichage du label de période */}
              <div className="text-white/60 text-xs mt-2">
                {monthlyStats?.period_label || formatMonth(selectedMonth)}
              </div>
            </div>
          </div>
          </>
        )}

        {/* Actions Bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button onClick={openNewDocModal} className="bg-indigo-500 hover:bg-indigo-600">
            <Plus className="w-4 h-4 mr-2" /> Ajouter document
          </Button>
          
          {/* Zone Drag & Drop + Boutons Import */}
          <div 
            ref={dropZoneRef}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`relative flex items-center gap-2 p-2 rounded-xl border-2 border-dashed transition-all ${
              isDragging 
                ? 'border-indigo-400 bg-indigo-500/20' 
                : 'border-white/20 hover:border-white/40'
            }`}
          >
            {isDragging && (
              <div className="absolute inset-0 flex items-center justify-center bg-indigo-500/30 backdrop-blur rounded-xl z-10">
                <div className="text-white font-medium flex items-center gap-2">
                  <Upload className="w-6 h-6 animate-bounce" />
                  Déposez vos fichiers ou dossiers ici
                </div>
              </div>
            )}
            
            {/* Import Fichiers */}
            <label className="cursor-pointer">
              <input
                type="file"
                multiple
                accept="*/*"
                onChange={handleBulkFileSelect}
                className="hidden"
              />
              <Button 
                as="span" 
                variant="outline" 
                className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 pointer-events-none"
              >
                <Upload className="w-4 h-4 mr-2" /> Fichiers
              </Button>
            </label>
            
            {/* Import Dossier */}
            <label className="cursor-pointer">
              <input
                type="file"
                webkitdirectory=""
                directory=""
                multiple
                onChange={handleBulkFileSelect}
                className="hidden"
              />
              <Button 
                as="span" 
                variant="outline" 
                className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10 pointer-events-none"
              >
                <FolderOpen className="w-4 h-4 mr-2" /> Dossier
              </Button>
            </label>
            
            {/* Import ZIP */}
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".zip,.rar,.7z,.tar,.gz"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setShowZipImportModal(true);
                    importZipFile(e.target.files[0]);
                  }
                }}
                className="hidden"
              />
              <Button 
                as="span" 
                variant="outline" 
                className="border-orange-500/50 text-orange-300 hover:bg-orange-500/10 pointer-events-none"
              >
                <FileArchive className="w-4 h-4 mr-2" /> ZIP
              </Button>
            </label>
            
            <span className="text-white/40 text-xs hidden md:inline">ou glisser-déposer</span>
          </div>
          
          {/* Détection problèmes */}
          <Button 
            onClick={() => {
              setShowIssuesModal(true);
              detectDocumentIssues();
            }} 
            variant="outline" 
            className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
          >
            <AlertTriangle className="w-4 h-4 mr-2" /> Vérifier IA
          </Button>
          
          {/* Réanalyse globale */}
          <Button 
            onClick={() => {
              setShowReanalyzeModal(true);
              loadReanalyzeStatus();
            }} 
            variant="outline" 
            className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Réanalyser Tout
          </Button>
          
          <Button 
            onClick={() => navigate('/bilan-dashboard')} 
            variant="outline" 
            className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
          >
            <PieChart className="w-4 h-4 mr-2" /> Tableau de Bord Bilan
          </Button>
          
          <Button 
            onClick={() => navigate('/bank-tracking')} 
            variant="outline" 
            className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10"
          >
            <Building className="w-4 h-4 mr-2" /> Suivi Banque
          </Button>
          
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher..."
              className="pl-10 bg-white/10 border-white/20 text-white"
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="all">Toutes catégories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          
          <Button onClick={() => setShowCategoryModal(true)} variant="outline" className="border-white/20 text-white">
            <FolderOpen className="w-4 h-4 mr-2" /> Catégories
          </Button>
          
          <Button 
            onClick={loadDuplicates} 
            variant="outline" 
            className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
            disabled={loadingDuplicates}
          >
            {loadingDuplicates ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Copy className="w-4 h-4 mr-2" />
            )}
            Doublons
          </Button>
          
          <Button onClick={openExportModal} variant="outline" className="border-white/20 text-white">
            <Download className="w-4 h-4 mr-2" /> Exporter
          </Button>
        </div>

        {/* Documents List */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-white">
                Documents ({filteredDocuments.length})
              </h2>
              
              {/* Sélection de documents */}
              {filteredDocuments.filter(d => d.has_file).length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleSelectAll}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                      selectedDocs.size === filteredDocuments.filter(d => d.has_file).length
                        ? 'bg-indigo-500 text-white'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {selectedDocs.size === filteredDocuments.filter(d => d.has_file).length ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                  
                  {selectedDocs.size > 0 && (
                    <span className="text-indigo-400 text-sm font-medium">
                      {selectedDocs.size} sélectionné(s)
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Actions d'export */}
            {selectedDocs.size > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-500/50 text-red-300 hover:bg-red-500/10"
                  onClick={deleteSelectedDocuments}
                  disabled={deletingDocs}
                >
                  {deletingDocs ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Supprimer
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  onClick={downloadZip}
                  disabled={preparingZip}
                >
                  {preparingZip ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <FileArchive className="w-4 h-4 mr-1" />
                  )}
                  ZIP
                </Button>
                <Button
                  size="sm"
                  className="bg-indigo-500 hover:bg-indigo-600"
                  onClick={openExportModal}
                >
                  <Send className="w-4 h-4 mr-1" />
                  Envoyer
                </Button>
              </div>
            )}
          </div>
          
          {filteredDocuments.length === 0 ? (
            <div className="p-12 text-center text-white/50">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun document pour ce mois</p>
              <Button onClick={openNewDocModal} className="mt-4 bg-indigo-500 hover:bg-indigo-600">
                <Plus className="w-4 h-4 mr-2" /> Ajouter le premier
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {filteredDocuments.map(doc => {
                const IconComponent = getCategoryIcon(doc.category_id);
                const isSelected = selectedDocs.has(doc.id);
                return (
                  <div key={doc.id} className={`p-4 flex items-center gap-4 hover:bg-white/5 transition ${isSelected ? 'bg-indigo-500/10' : ''}`}>
                    {/* Checkbox pour sélection (uniquement si fichier attaché) */}
                    {doc.has_file && (
                      <button
                        onClick={() => toggleDocSelection(doc.id)}
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center transition ${
                          isSelected 
                            ? 'bg-indigo-500 border-indigo-500' 
                            : 'border-white/30 hover:border-indigo-400'
                        }`}
                      >
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </button>
                    )}
                    {!doc.has_file && <div className="w-6" />}
                    
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: getCategoryColor(doc.category_id) + '30' }}
                    >
                      <IconComponent className="w-5 h-5" style={{ color: getCategoryColor(doc.category_id) }} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white truncate flex items-center gap-2">
                        {doc.title}
                        {/* Badge ENVOYÉ si le document a été envoyé */}
                        {doc.sent_at && (
                          <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Envoyé
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-white/60 flex items-center gap-2 flex-wrap">
                        <span>{getCategoryName(doc.category_id)}</span>
                        {doc.supplier && <span>• {doc.supplier}</span>}
                        {doc.reference && <span>• Réf: {doc.reference}</span>}
                        {!doc.has_file && <span className="text-amber-400/70">• Sans fichier</span>}
                        {doc.sent_at && (
                          <span className="text-green-400/70">
                            • Envoyé le {new Date(doc.sent_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="font-semibold text-white">{formatCurrency(doc.amount)}</div>
                      <div className="text-xs text-white/50">{doc.date}</div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      {doc.has_file && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-blue-400 hover:text-blue-300"
                          onClick={() => viewDocument(doc)}
                          title="Visualiser le document"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-white/70" onClick={() => openEditModal(doc)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteDocument(doc.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Ajout/Modification Document */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white">
                    {editingDoc ? 'Modifier le document' : 'Nouveau document'}
                  </h2>
                  <Button onClick={() => {
                    setShowModal(false);
                    setAiAnalysisResult(null);
                  }} variant="ghost" size="sm" className="text-gray-400">
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-4">
                  {/* Upload fichier EN PREMIER */}
                  <div>
                    <label className="text-sm font-medium text-white/80 mb-2 block">1. Document à importer</label>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error('Fichier trop volumineux (max 10 Mo)');
                            return;
                          }
                          
                          const reader = new FileReader();
                          reader.onload = async (event) => {
                            const base64 = event.target.result.split(',')[1];
                            setFormData(prev => ({
                              ...prev,
                              file_data: base64,
                              file_name: file.name,
                              file_type: file.type
                            }));
                            setAiAnalysisResult(null);
                            toast.success(`"${file.name}" sélectionné`);
                            
                            // Analyse automatique si activée
                            if (autoAnalyze && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
                              setAnalyzingDocument(true);
                              try {
                                const response = await axios.post(`${API}/api/ai-document/analyze`, {
                                  file_data: base64,
                                  file_name: file.name,
                                  file_type: file.type
                                }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                
                                if (response.data.success) {
                                  setAiAnalysisResult(response.data);
                                  const data = response.data.extracted_data;
                                  
                                  // Trouver la catégorie correspondante
                                  let categoryId = formData.category_id;
                                  if (response.data.category_id) {
                                    categoryId = response.data.category_id;
                                  } else if (response.data.suggested_category) {
                                    const matchCat = categories.find(c => 
                                      c.name.toLowerCase().includes(response.data.suggested_category.toLowerCase()) ||
                                      response.data.suggested_category.toLowerCase().includes(c.name.toLowerCase())
                                    );
                                    if (matchCat) categoryId = matchCat.id;
                                  }
                                  
                                  setFormData(prev => ({
                                    ...prev,
                                    title: data.description || data.fournisseur || prev.title || file.name.replace(/\.[^/.]+$/, ''),
                                    amount: data.montant ? data.montant.toString() : prev.amount,
                                    date: data.date || prev.date,
                                    supplier: data.fournisseur || prev.supplier,
                                    reference: data.reference || prev.reference,
                                    category_id: categoryId
                                  }));
                                  
                                  toast.success(`✅ Analysé ! Confiance: ${data.confiance}/10 - Cliquez sur "Enregistrer" pour sauvegarder`, {
                                    duration: 5000
                                  });
                                  // Avertir si l'IA n'a pas réussi à lire le montant
                                  if (!data.montant || data.montant === 0) {
                                    toast.warning('⚠️ Montant non détecté par l\'IA. Saisissez-le manuellement avant d\'enregistrer.', {
                                      duration: 7000
                                    });
                                  }
                                  if (response.data.category_created) {
                                    toast.info(`Nouvelle catégorie créée: ${response.data.suggested_category}`);
                                    loadData();
                                  }
                                }
                              } catch (error) {
                                console.error('Erreur analyse auto:', error);
                                const errorMsg = error.response?.data?.detail || 'Analyse échouée';
                                toast.error(errorMsg);
                              } finally {
                                setAnalyzingDocument(false);
                              }
                            }
                          };
                          reader.readAsDataURL(file);
                        }}
                        accept="image/*,.pdf"
                        className="hidden"
                      />
                      <input
                        type="file"
                        ref={cameraInputRef}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          const reader = new FileReader();
                          reader.onload = async (event) => {
                            const base64 = event.target.result.split(',')[1];
                            setFormData(prev => ({
                              ...prev,
                              file_data: base64,
                              file_name: file.name,
                              file_type: file.type
                            }));
                            setAiAnalysisResult(null);
                            toast.success('Photo capturée');
                            
                            // Analyse automatique si activée
                            if (autoAnalyze) {
                              setAnalyzingDocument(true);
                              try {
                                const response = await axios.post(`${API}/api/ai-document/analyze`, {
                                  file_data: base64,
                                  file_name: file.name,
                                  file_type: file.type
                                }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                
                                if (response.data.success) {
                                  setAiAnalysisResult(response.data);
                                  const data = response.data.extracted_data;
                                  
                                  let categoryId = formData.category_id;
                                  if (response.data.category_id) {
                                    categoryId = response.data.category_id;
                                  }
                                  
                                  setFormData(prev => ({
                                    ...prev,
                                    title: data.description || data.fournisseur || prev.title,
                                    amount: data.montant ? data.montant.toString() : prev.amount,
                                    date: data.date || prev.date,
                                    supplier: data.fournisseur || prev.supplier,
                                    reference: data.reference || prev.reference,
                                    category_id: categoryId
                                  }));
                                  
                                  toast.success(`Analysé ! Confiance: ${data.confiance}/10`);
                                  // Avertir si l'IA n'a pas réussi à lire le montant
                                  if (!data.montant || data.montant === 0) {
                                    toast.warning('⚠️ Montant non détecté par l\'IA. Saisissez-le manuellement avant d\'enregistrer.', {
                                      duration: 7000
                                    });
                                  }
                                }
                              } catch (error) {
                                toast.error('Analyse auto échouée');
                              } finally {
                                setAnalyzingDocument(false);
                              }
                            }
                          };
                          reader.readAsDataURL(file);
                        }}
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                      />
                      
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 border-white/20 text-white"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Fichier
                      </Button>
                      
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 border-white/20 text-white"
                        onClick={() => cameraInputRef.current?.click()}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Scanner
                      </Button>
                    </div>
                    
                    {formData.file_name && (
                      <div className="mt-2 p-2 bg-green-500/20 rounded-lg text-green-300 text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {formData.file_name}
                        {analyzingDocument && (
                          <span className="ml-auto flex items-center text-purple-300">
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Analyse...
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Option analyse auto */}
                    <div className="mt-2 flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoAnalyze}
                          onChange={(e) => setAutoAnalyze(e.target.checked)}
                          className="rounded border-white/20"
                        />
                        <Zap className="w-3 h-3 text-yellow-400" />
                        Analyse automatique
                      </label>
                      
                      {formData.file_data && !analyzingDocument && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-purple-400 hover:text-purple-300"
                          onClick={async () => {
                            setAnalyzingDocument(true);
                            try {
                              const response = await axios.post(`${API}/api/ai-document/analyze`, {
                                file_data: formData.file_data,
                                file_name: formData.file_name,
                                file_type: formData.file_type
                              }, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              
                              if (response.data.success) {
                                setAiAnalysisResult(response.data);
                                const data = response.data.extracted_data;
                                
                                let categoryId = formData.category_id;
                                if (response.data.category_id) {
                                  categoryId = response.data.category_id;
                                }
                                
                                setFormData(prev => ({
                                  ...prev,
                                  title: data.description || data.fournisseur || prev.title,
                                  amount: data.montant ? data.montant.toString() : prev.amount,
                                  date: data.date || prev.date,
                                  supplier: data.fournisseur || prev.supplier,
                                  reference: data.reference || prev.reference,
                                  category_id: categoryId
                                }));
                                
                                toast.success(`Analysé ! Confiance: ${data.confiance}/10`);
                              }
                            } catch (error) {
                              console.error('Erreur analyse IA:', error);
                              const errorMsg = error.response?.data?.detail || 'Erreur analyse IA';
                              toast.error(errorMsg);
                            } finally {
                              setAnalyzingDocument(false);
                            }
                          }}
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          Analyser IA
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Résultat analyse IA */}
                  {aiAnalysisResult && (
                    <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-purple-300 text-xs font-medium mb-2">
                        <Sparkles className="w-3 h-3" />
                        Analyse IA Claude
                        <span className="ml-auto bg-purple-500/30 px-1.5 py-0.5 rounded">
                          Confiance: {aiAnalysisResult.extracted_data.confiance}/10
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {aiAnalysisResult.extracted_data.type_document && (
                          <div className="text-white/70">
                            Type: <span className={`font-medium ${aiAnalysisResult.extracted_data.est_avoir ? 'text-orange-400' : 'text-white'}`}>
                              {aiAnalysisResult.extracted_data.type_document}
                              {aiAnalysisResult.extracted_data.est_avoir && ' (AVOIR)'}
                            </span>
                          </div>
                        )}
                        {aiAnalysisResult.suggested_category && (
                          <div className="text-white/70">
                            Catégorie: <span className="text-purple-300">{aiAnalysisResult.suggested_category}</span>
                            {aiAnalysisResult.category_created && <span className="text-green-400 ml-1">(nouvelle!)</span>}
                          </div>
                        )}
                        {aiAnalysisResult.extracted_data.montant_ht && (
                          <div className="text-white/70">
                            HT: <span className="text-white">{aiAnalysisResult.extracted_data.montant_ht?.toFixed(2)} €</span>
                          </div>
                        )}
                        {aiAnalysisResult.extracted_data.montant_tva && (
                          <div className="text-white/70">
                            TVA: <span className="text-white">{aiAnalysisResult.extracted_data.montant_tva?.toFixed(2)} €</span>
                          </div>
                        )}
                        {aiAnalysisResult.extracted_data.reference && (
                          <div className="text-white/70 col-span-2">
                            Réf: <span className="text-white font-mono">{aiAnalysisResult.extracted_data.reference}</span>
                          </div>
                        )}
                      </div>
                      {/* Alertes de l'IA */}
                      {aiAnalysisResult.extracted_data.alertes?.length > 0 && (
                        <div className="mt-2 p-2 bg-yellow-500/20 border border-yellow-500/30 rounded text-xs text-yellow-300">
                          ⚠️ {aiAnalysisResult.extracted_data.alertes.join(' | ')}
                        </div>
                      )}
                      {/* Indication montant négatif pour avoir */}
                      {aiAnalysisResult.extracted_data.est_avoir && (
                        <div className="mt-2 p-2 bg-orange-500/20 border border-orange-500/30 rounded text-xs text-orange-300">
                          💡 Avoir détecté: le montant est négatif ({aiAnalysisResult.extracted_data.montant?.toFixed(2)} €)
                        </div>
                      )}
                      
                      {/* Documents multiples détectés */}
                      {aiAnalysisResult.extracted_data.est_document_multiple && aiAnalysisResult.extracted_data.sous_documents?.length > 0 && (
                        <div className="mt-3 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-blue-400" />
                            <span className="text-sm font-semibold text-blue-300">
                              Document multiple détecté ({aiAnalysisResult.extracted_data.sous_documents.length} sections)
                            </span>
                          </div>
                          <p className="text-xs text-white/60 mb-3">
                            Ce document contient plusieurs sections avec des totaux distincts. Vous pouvez les importer séparément.
                          </p>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {aiAnalysisResult.extracted_data.sous_documents.map((subDoc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-white/5 rounded border border-white/10">
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-white">{subDoc.titre}</div>
                                  <div className="text-xs text-white/50">
                                    {subDoc.date_debut && subDoc.date_fin 
                                      ? `${subDoc.date_debut} → ${subDoc.date_fin}` 
                                      : subDoc.description}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-bold text-emerald-400">{subDoc.montant_ttc?.toFixed(2)} €</div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-xs text-blue-400 hover:text-blue-300 p-1 h-auto"
                                    onClick={() => {
                                      // Créer un document séparé pour cette section
                                      setFormData({
                                        ...formData,
                                        title: subDoc.titre,
                                        amount: subDoc.montant_ttc?.toString() || '',
                                        date: subDoc.date_debut || formData.date,
                                        reference: subDoc.reference || '',
                                        supplier: subDoc.fournisseur || formData.supplier,
                                        notes: `Période: ${subDoc.date_debut || ''} - ${subDoc.date_fin || ''}\n${subDoc.description || ''}`
                                      });
                                      toast.info(`Section "${subDoc.titre}" sélectionnée`);
                                    }}
                                  >
                                    Utiliser
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 pt-2 border-t border-white/10 flex justify-between items-center">
                            <span className="text-xs text-white/50">Total combiné:</span>
                            <span className="text-sm font-bold text-white">
                              {aiAnalysisResult.extracted_data.sous_documents.reduce((sum, s) => sum + (s.montant_ttc || 0), 0).toFixed(2)} €
                            </span>
                          </div>
                          <Button
                            size="sm"
                            className="w-full mt-2 bg-blue-600 hover:bg-blue-700"
                            onClick={async () => {
                              // Créer tous les sous-documents en une fois
                              const subDocs = aiAnalysisResult.extracted_data.sous_documents;
                              let created = 0;
                              for (const subDoc of subDocs) {
                                try {
                                  await axios.post(`${API}/api/accounting/documents`, {
                                    title: subDoc.titre,
                                    amount: subDoc.montant_ttc,
                                    date: subDoc.date_debut || formData.date,
                                    category_id: formData.category_id,
                                    supplier: subDoc.fournisseur || formData.supplier,
                                    reference: subDoc.reference || '',
                                    notes: `Période: ${subDoc.date_debut || ''} - ${subDoc.date_fin || ''}\n${subDoc.description || ''}`,
                                    type: subDoc.type_document || 'frais_bancaires',
                                    file_data: uploadedFile?.data,
                                    file_name: uploadedFile?.name,
                                    file_type: uploadedFile?.type,
                                    has_file: !!uploadedFile
                                  }, { headers: { Authorization: `Bearer ${token}` } });
                                  created++;
                                } catch (e) {
                                  console.error('Erreur création sous-document:', e);
                                }
                              }
                              toast.success(`${created}/${subDocs.length} documents créés`);
                              setShowImportModal(false);
                              setUploadedFile(null);
                              setAiAnalysisResult(null);
                              loadData();
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Créer les {aiAnalysisResult.extracted_data.sous_documents.length} documents séparément
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Champs modifiables */}
                  <div className="border-t border-white/10 pt-4">
                    <label className="text-sm font-medium text-white/80 mb-2 block">2. Vérifier et compléter</label>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-white/80 mb-1 block">Titre *</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="Ex: Facture fournisseur pièces"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Catégorie *</label>
                      <select
                        value={formData.category_id}
                        onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                      >
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Montant TTC *</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        placeholder="0.00"
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Date *</label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({...formData, date: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-white/80 mb-1 block">Fournisseur</label>
                      <Input
                        value={formData.supplier}
                        onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                        placeholder="Nom du fournisseur"
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-white/80 mb-1 block">Référence</label>
                    <Input
                      value={formData.reference}
                      onChange={(e) => setFormData({...formData, reference: e.target.value})}
                      placeholder="N° de facture"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-white/80 mb-1 block">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Notes ou commentaires..."
                      rows={2}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white resize-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button onClick={() => {
                    setShowModal(false);
                    setAiAnalysisResult(null);
                  }} variant="outline" className="flex-1 border-white/20 text-white">
                    Annuler
                  </Button>
                  <Button 
                    onClick={() => {
                      saveDocument();
                    }} 
                    disabled={savingDocument}
                    className={`flex-1 ${
                      savingDocument
                        ? 'bg-gray-500 cursor-wait'
                        : aiAnalysisResult 
                          ? 'bg-emerald-500 hover:bg-emerald-600 animate-pulse' 
                          : 'bg-indigo-500 hover:bg-indigo-600'
                    }`}
                  >
                    {savingDocument ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {aiAnalysisResult ? '⬇️ Enregistrer maintenant' : (editingDoc ? 'Modifier' : 'Enregistrer')}
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Rappel après analyse */}
                {aiAnalysisResult && (
                  <div className="mt-3 bg-emerald-500/20 border border-emerald-500/40 rounded-lg p-3 text-center">
                    <p className="text-emerald-300 text-sm font-medium">
                      ✅ Document analysé avec succès ! Cliquez sur "Enregistrer" pour le sauvegarder.
                    </p>
                  </div>
                )}
                
                {/* Info sécurité */}
                <div className="mt-3 text-xs text-white/40 text-center">
                  <Sparkles className="w-3 h-3 inline mr-1" />
                  IA Claude - Documents privés et sécurisés
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Catégories */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white">Catégories</h2>
                  <Button onClick={() => setShowCategoryModal(false)} variant="ghost" size="sm" className="text-gray-400">
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Liste des catégories */}
                <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                  {categories.map(cat => (
                    <div key={cat.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }}></div>
                      <span className="flex-1 text-white">{cat.name}</span>
                      {cat.system && <span className="text-xs text-white/40">Système</span>}
                    </div>
                  ))}
                </div>

                {/* Ajouter catégorie */}
                <div className="border-t border-white/10 pt-4">
                  <h3 className="text-sm font-medium text-white/80 mb-3">Nouvelle catégorie</h3>
                  <div className="flex gap-2">
                    <Input
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                      placeholder="Nom de la catégorie"
                      className="flex-1 bg-white/10 border-white/20 text-white"
                    />
                    <input
                      type="color"
                      value={newCategory.color}
                      onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Button onClick={addCategory} className="bg-indigo-500 hover:bg-indigo-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Export - Nouveau avec sélection et archivage */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-lg w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Send className="w-5 h-5 text-indigo-400" />
                    Envoyer au Comptable
                  </h2>
                  <Button onClick={() => setShowExportModal(false)} variant="ghost" size="sm" className="text-gray-400">
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Résumé sélection */}
                <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 text-white mb-2">
                    <FileArchive className="w-5 h-5 text-indigo-400" />
                    <span className="font-semibold">{selectedDocs.size} document(s) sélectionné(s)</span>
                  </div>
                  <p className="text-white/60 text-sm">
                    Période: <strong>{formatMonth(selectedMonth)}</strong>
                  </p>
                  <p className="text-white/50 text-xs mt-1">
                    Les fichiers seront compressés en ZIP et archivés pour traçabilité.
                  </p>
                </div>

                {/* Alerte documents déjà envoyés */}
                {checkingSentStatus ? (
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-white/50" />
                    <span className="text-white/50 text-sm">Vérification du statut...</span>
                  </div>
                ) : alreadySentDocs.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-amber-300 font-medium text-sm">
                          {alreadySentDocs.length} document(s) déjà envoyé(s)
                        </p>
                        <p className="text-white/60 text-xs mt-1">
                          Ces documents ont déjà été transmis au comptable. Vous pouvez les renvoyer si nécessaire.
                        </p>
                        <div className="mt-2 space-y-1 max-h-24 overflow-y-auto">
                          {alreadySentDocs.map(doc => (
                            <div key={doc.id} className="text-xs text-white/50 flex items-center gap-1">
                              <span>•</span>
                              <span className="truncate">{doc.title}</span>
                              <span className="text-amber-400/70">
                                (envoyé le {new Date(doc.sent_at).toLocaleDateString('fr-FR')})
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Email destinataire */}
                <div className="mb-4">
                  <label className="text-sm text-white/70 mb-2 block">Email du comptable *</label>
                  <Input
                    type="email"
                    value={accountantEmail}
                    onChange={(e) => setAccountantEmail(e.target.value)}
                    placeholder="comptable@exemple.fr"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                {/* Message pour le comptable */}
                <div className="mb-4">
                  <label className="text-sm text-white/70 mb-2 block">Message (optionnel)</label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Ex: Documents du mois d'avril, facture X en attente..."
                    rows={2}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none"
                  />
                </div>

                <div className="flex flex-col gap-3">
                  {/* Info Resend */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-300">
                    <strong>💡 Note :</strong> L'envoi par email nécessite un domaine vérifié sur Resend. 
                    Sinon, utilisez "Télécharger ZIP" ci-dessous.
                  </div>
                  
                  {/* Bouton Envoyer */}
                  <Button 
                    onClick={sendToAccountant}
                    disabled={sendingEmail || selectedDocs.size === 0}
                    className="w-full bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50"
                  >
                    {sendingEmail ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Compression et envoi...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer le ZIP
                      </>
                    )}
                  </Button>

                  <div className="flex gap-3">
                    <Button 
                      onClick={() => setShowExportModal(false)} 
                      variant="outline" 
                      className="flex-1 border-white/20 text-white"
                    >
                      Annuler
                    </Button>
                    <Button 
                      onClick={downloadZip}
                      disabled={preparingZip || selectedDocs.size === 0}
                      variant="outline"
                      className="flex-1 border-white/20 text-white"
                    >
                      {preparingZip ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Télécharger ZIP
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Historique des Envois */}
        {showArchivesModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-emerald-400" />
                    Historique des Envois
                  </h2>
                  <Button onClick={() => setShowArchivesModal(false)} variant="ghost" size="sm" className="text-gray-400">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {loadingArchives ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-400 mx-auto mb-2" />
                    <p className="text-white/60">Chargement...</p>
                  </div>
                ) : archives.length === 0 ? (
                  <div className="text-center py-8 text-white/50">
                    <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucun envoi archivé</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {archives.map(archive => (
                      <div 
                        key={archive.id}
                        className="bg-white/5 border border-white/10 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FileArchive className="w-4 h-4 text-emerald-400" />
                            <span className="font-medium text-white">{archive.file_name}</span>
                          </div>
                          <span className="text-xs text-white/50">
                            {new Date(archive.export_date).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div className="text-white/60">
                            <span className="text-white/40">Mois:</span>{' '}
                            <span className="text-white">{archive.month}</span>
                          </div>
                          <div className="text-white/60">
                            <span className="text-white/40">Documents:</span>{' '}
                            <span className="text-white">{archive.document_count}</span>
                          </div>
                          <div className="text-white/60">
                            <span className="text-white/40">Total:</span>{' '}
                            <span className="text-emerald-400">{formatCurrency(archive.total_amount)}</span>
                          </div>
                        </div>
                        
                        <div className="mt-2 text-xs text-white/40 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {archive.recipient_email}
                        </div>
                        
                        {archive.message && (
                          <div className="mt-2 text-xs text-white/50 italic bg-white/5 px-2 py-1 rounded">
                            "{archive.message}"
                          </div>
                        )}
                        
                        {/* Actions sur l'archive */}
                        <div className="mt-3 pt-3 border-t border-white/10 flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                            onClick={async () => {
                              // Re-télécharger le ZIP avec les mêmes documents
                              if (!archive.document_ids || archive.document_ids.length === 0) {
                                toast.error('Liste des documents non disponible');
                                return;
                              }
                              setPreparingZip(true);
                              try {
                                const response = await axios.post(`${API}/api/ai-document/export/prepare-zip`, {
                                  document_ids: archive.document_ids,
                                  month: archive.month
                                }, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
                                
                                const byteCharacters = atob(response.data.zip_data);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], { type: 'application/zip' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = archive.file_name || `Comptabilite_${archive.month}.zip`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                                toast.success('ZIP re-téléchargé');
                              } catch (error) {
                                toast.error('Erreur lors du téléchargement');
                              } finally {
                                setPreparingZip(false);
                              }
                            }}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Re-télécharger
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                            onClick={() => {
                              // Sélectionner les documents de cette archive
                              if (archive.document_ids && archive.document_ids.length > 0) {
                                setSelectedDocs(new Set(archive.document_ids));
                                setSelectedMonth(archive.month);
                                setShowArchivesModal(false);
                                toast.info(`${archive.document_ids.length} documents sélectionnés`);
                              } else {
                                toast.error('Liste des documents non disponible');
                              }
                            }}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Re-sélectionner
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal Doublons */}
        {showDuplicatesModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-3xl w-full max-h-[85vh] overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Copy className="w-5 h-5 text-amber-400" />
                    Détection des Doublons
                    <span className="text-sm font-normal text-white/50 ml-2">
                      {formatMonth(selectedMonth)}
                    </span>
                  </h2>
                  <Button onClick={() => setShowDuplicatesModal(false)} variant="ghost" size="sm" className="text-gray-400">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[65vh]">
                {loadingDuplicates ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto mb-2" />
                    <p className="text-white/60">Analyse en cours...</p>
                  </div>
                ) : duplicateGroups.length === 0 ? (
                  <div className="text-center py-8 text-white/50">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
                    <p className="text-lg font-medium text-white">Aucun doublon détecté</p>
                    <p className="text-sm mt-2">Tous vos documents sont uniques pour ce mois.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                        <div>
                          <p className="text-amber-300 font-medium">
                            {duplicateGroups.length} groupe(s) de doublons détecté(s)
                          </p>
                          <p className="text-white/60 text-sm">
                            Critères: même fournisseur + même montant + même date
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {duplicateGroups.map((group, groupIdx) => (
                      <div key={group.key} className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                        <div className="bg-white/5 p-3 border-b border-white/10 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="bg-amber-500/20 text-amber-300 px-2 py-1 rounded text-xs font-medium">
                              {group.count} doublons
                            </span>
                            <span className="text-white font-medium">{group.supplier || 'Non spécifié'}</span>
                            <span className="text-white/50">•</span>
                            <span className="text-emerald-400 font-medium">{formatCurrency(group.amount)}</span>
                            <span className="text-white/50">•</span>
                            <span className="text-white/60">{group.date}</span>
                          </div>
                        </div>
                        
                        <div className="divide-y divide-white/5">
                          {group.documents.map((doc, docIdx) => (
                            <div key={doc.id} className="p-3 flex items-center gap-3 hover:bg-white/5">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-white text-sm truncate">{doc.title}</span>
                                  {doc.sent_at && (
                                    <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded-full">
                                      Envoyé
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-white/50 mt-1">
                                  {doc.reference && <span>Réf: {doc.reference} • </span>}
                                  Ajouté le {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                                </div>
                              </div>
                              
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                                onClick={() => {
                                  if (window.confirm(`Supprimer "${doc.title}" ?`)) {
                                    bulkDeleteDocuments([doc.id]);
                                  }
                                }}
                                disabled={deletingDocs}
                              >
                                {deletingDocs ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          ))}
                        </div>
                        
                        {/* Actions groupées */}
                        <div className="p-3 bg-white/5 border-t border-white/10 flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                            onClick={() => {
                              // Garder le premier, supprimer les autres
                              const toDelete = group.documents.slice(1).map(d => d.id);
                              if (toDelete.length > 0 && window.confirm(`Garder le premier et supprimer ${toDelete.length} autre(s) ?`)) {
                                bulkDeleteDocuments(toDelete);
                              }
                            }}
                            disabled={deletingDocs}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Garder 1er, supprimer {group.count - 1} autre(s)
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-white/10 flex justify-end">
                <Button onClick={() => setShowDuplicatesModal(false)} variant="outline" className="border-white/20 text-white">
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Import Multiple */}
        {showBulkImportModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-2xl w-full max-h-[85vh] overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Upload className="w-5 h-5 text-emerald-400" />
                    Import Multiple
                    <span className="text-sm font-normal text-white/50 ml-2">
                      {bulkFiles.length} fichier(s)
                    </span>
                  </h2>
                  <Button 
                    onClick={() => {
                      setShowBulkImportModal(false);
                      setBulkFiles([]);
                      setBulkImportProgress({ current: 0, total: 0, results: [] });
                    }} 
                    variant="ghost" 
                    size="sm" 
                    className="text-gray-400"
                    disabled={isBulkImporting}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[55vh]">
                {/* Liste des fichiers */}
                <div className="space-y-2 mb-4">
                  {bulkFiles.map((file, idx) => {
                    const result = bulkImportProgress.results[idx];
                    const isProcessing = isBulkImporting && bulkImportProgress.current === idx + 1 && !result;
                    
                    return (
                      <div 
                        key={idx} 
                        className={`flex items-center gap-3 p-3 rounded-lg border ${
                          result?.status === 'success' 
                            ? 'bg-emerald-500/10 border-emerald-500/30' 
                            : result?.status === 'error'
                            ? 'bg-red-500/10 border-red-500/30'
                            : isProcessing
                            ? 'bg-blue-500/10 border-blue-500/30'
                            : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <div className="flex-shrink-0">
                          {result?.status === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                          ) : result?.status === 'error' ? (
                            <X className="w-5 h-5 text-red-400" />
                          ) : isProcessing ? (
                            <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                          ) : (
                            <FileText className="w-5 h-5 text-white/50" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate" title={file.relativePath || file.name}>
                            {file.relativePath || file.name}
                          </p>
                          <p className="text-white/50 text-xs">
                            {(file.size / 1024).toFixed(0)} KB • {file.type || 'Type inconnu'}
                            {result?.status === 'success' && result.data?.fournisseur && (
                              <span className="text-emerald-400 ml-2">
                                → {result.data.fournisseur} ({result.data.montant}€)
                              </span>
                            )}
                            {result?.status === 'error' && (
                              <span className="text-red-400 ml-2">
                                → {result.error}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Barre de progression */}
                {isBulkImporting && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-white/60 mb-1">
                      <span>Progression</span>
                      <span>{bulkImportProgress.current} / {bulkImportProgress.total}</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-emerald-500 transition-all duration-300"
                        style={{ width: `${(bulkImportProgress.current / bulkImportProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Résumé final */}
                {!isBulkImporting && bulkImportProgress.results.length > 0 && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <h3 className="text-white font-medium mb-2">Résumé</h3>
                    <div className="flex gap-4 text-sm">
                      <span className="text-emerald-400">
                        ✓ {bulkImportProgress.results.filter(r => r.status === 'success').length} importé(s)
                      </span>
                      <span className="text-red-400">
                        ✗ {bulkImportProgress.results.filter(r => r.status === 'error').length} erreur(s)
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-white/10 flex justify-end gap-3">
                {!isBulkImporting && bulkImportProgress.results.length === 0 ? (
                  <>
                    <Button 
                      onClick={() => {
                        setShowBulkImportModal(false);
                        setBulkFiles([]);
                      }} 
                      variant="outline" 
                      className="border-white/20 text-white"
                    >
                      Annuler
                    </Button>
                    <Button 
                      onClick={processBulkImport}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Analyser et Importer ({bulkFiles.length})
                    </Button>
                  </>
                ) : (
                  <Button 
                    onClick={() => {
                      setShowBulkImportModal(false);
                      setBulkFiles([]);
                      setBulkImportProgress({ current: 0, total: 0, results: [] });
                    }} 
                    variant="outline" 
                    className="border-white/20 text-white"
                    disabled={isBulkImporting}
                  >
                    {isBulkImporting ? 'Import en cours...' : 'Fermer'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal Vérification IA - Détection des problèmes */}
        {showIssuesModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Vérification IA - Documents à problèmes
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowIssuesModal(false)}
                  className="text-white/60 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {loadingIssues ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
                  <span className="ml-3 text-white/60">Analyse en cours...</span>
                </div>
              ) : documentIssues?.issues?.length > 0 ? (
                <>
                  {/* Résumé des types de problèmes */}
                  <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex flex-wrap gap-3 text-sm">
                      <span className="text-amber-300 font-medium">
                        {documentIssues.issues.length} document(s) avec problèmes
                      </span>
                      {documentIssues.issue_types?.date_trop_ancienne > 0 && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded">
                          {documentIssues.issue_types.date_trop_ancienne} date(s) ancienne(s)
                        </span>
                      )}
                      {documentIssues.issue_types?.montant_manquant > 0 && (
                        <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded">
                          {documentIssues.issue_types.montant_manquant} montant(s) manquant(s)
                        </span>
                      )}
                      {documentIssues.issue_types?.categorie_autre > 0 && (
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                          {documentIssues.issue_types.categorie_autre} catégorie(s) "Autre"
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bouton correction automatique dates */}
                  {documentIssues.issue_types?.date_trop_ancienne > 0 && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-red-300 font-medium">Dates aberrantes détectées</p>
                        <p className="text-red-200/60 text-sm">
                          {documentIssues.issue_types.date_trop_ancienne} document(s) avec des dates trop anciennes
                        </p>
                      </div>
                      <Button
                        onClick={async () => {
                          try {
                            const response = await axios.post(`${API}/api/ai-document/fix-dates-auto`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (response.data.corrected_count > 0) {
                              toast.success(`${response.data.corrected_count} date(s) corrigée(s) automatiquement`);
                              loadData();
                              detectDocumentIssues();
                            } else {
                              toast.info('Aucune date à corriger');
                            }
                          } catch (error) {
                            toast.error('Erreur lors de la correction automatique');
                          }
                        }}
                        size="sm"
                        className="bg-red-500 hover:bg-red-600"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Corriger auto
                      </Button>
                    </div>
                  )}

                  <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                    {documentIssues.issues.map((issue) => (
                      <div 
                        key={issue.id} 
                        className="bg-white/5 border border-white/10 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-white font-medium">{issue.title || 'Sans titre'}</span>
                              {issue.issues?.map((issueType, idx) => (
                                <span key={idx} className={`px-2 py-0.5 rounded-full text-xs ${
                                  issueType === 'date_trop_ancienne' ? 'bg-red-500/20 text-red-400' :
                                  issueType === 'date_future' ? 'bg-purple-500/20 text-purple-400' :
                                  issueType === 'montant_manquant' ? 'bg-orange-500/20 text-orange-400' :
                                  issueType === 'categorie_autre' ? 'bg-blue-500/20 text-blue-400' :
                                  issueType === 'confiance_faible' ? 'bg-yellow-500/20 text-yellow-400' :
                                  issueType === 'fournisseur_manquant' ? 'bg-cyan-500/20 text-cyan-400' :
                                  'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {issueType === 'date_trop_ancienne' ? 'Date trop ancienne' :
                                   issueType === 'date_future' ? 'Date future' :
                                   issueType === 'montant_manquant' ? 'Montant = 0' :
                                   issueType === 'categorie_autre' ? 'Catégorie Autre' :
                                   issueType === 'confiance_faible' ? 'Confiance faible' :
                                   issueType === 'fournisseur_manquant' ? 'Fournisseur ?' :
                                   issueType}
                                </span>
                              ))}
                            </div>
                            <div className="text-white/50 text-sm flex flex-wrap gap-3">
                              {issue.supplier && <span>📦 {issue.supplier}</span>}
                              {issue.amount !== undefined && <span>💰 {issue.amount}€</span>}
                              {issue.date && (
                                <span className={issue.issues?.includes('date_trop_ancienne') ? 'text-red-400' : ''}>
                                  📅 {issue.date}
                                </span>
                              )}
                            </div>
                            {issue.issue_details?.length > 0 && (
                              <div className="text-white/40 text-xs mt-2 space-y-0.5">
                                {issue.issue_details.map((detail, idx) => (
                                  <div key={idx}>⚠️ {detail}</div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2 ml-3">
                            {/* Correction manuelle de date */}
                            {issue.issues?.includes('date_trop_ancienne') && (
                              <Button
                                onClick={async () => {
                                  const newDate = prompt('Nouvelle date (YYYY-MM-DD):', new Date().toISOString().slice(0, 10));
                                  if (newDate && /^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
                                    try {
                                      await axios.post(`${API}/api/ai-document/fix-date/${issue.id}`, 
                                        { new_date: newDate },
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success('Date corrigée');
                                      loadData();
                                      detectDocumentIssues();
                                    } catch (error) {
                                      toast.error('Erreur lors de la correction');
                                    }
                                  }
                                }}
                                size="sm"
                                variant="outline"
                                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                              >
                                <Calendar className="w-4 h-4 mr-1" />
                                Corriger date
                              </Button>
                            )}
                            
                            {issue.has_file && (
                              <Button
                                onClick={() => reanalyzeDocument(issue.id)}
                                disabled={reanalyzingDocs.has(issue.id)}
                                size="sm"
                                className="bg-amber-500 hover:bg-amber-600"
                              >
                                {reanalyzingDocs.has(issue.id) ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-1" />
                                    Réanalyser
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-white/10">
                    <Button
                      onClick={() => setShowIssuesModal(false)}
                      variant="outline"
                      className="flex-1 border-white/20 text-white"
                    >
                      Fermer
                    </Button>
                    <Button
                      onClick={reanalyzeAllIssues}
                      className="flex-1 bg-amber-500 hover:bg-amber-600"
                      disabled={!documentIssues?.issues?.some(i => i.has_file)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Tout réanalyser ({documentIssues.issues.filter(i => i.has_file).length})
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-400 opacity-50" />
                  <p className="text-white/60 text-lg">Aucun problème détecté</p>
                  <p className="text-white/40 text-sm mt-2">
                    Tous les documents semblent correctement analysés.
                  </p>
                  <Button
                    onClick={() => setShowIssuesModal(false)}
                    variant="outline"
                    className="mt-6 border-white/20 text-white"
                  >
                    Fermer
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Réanalyse Globale */}
        {showReanalyzeModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-emerald-400" />
                  Réanalyse Globale IA (GPT-5.2)
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setShowReanalyzeModal(false);
                    setReanalyzeProgress(null);
                  }}
                  className="text-white/60 hover:text-white"
                  disabled={isReanalyzing}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Information */}
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <p className="text-emerald-300 text-sm">
                  🚀 Réanalyse tous vos documents avec GPT-5.2 pour corriger automatiquement les erreurs de dates, montants et fournisseurs.
                </p>
              </div>

              {/* Statut par mois */}
              {reanalyzeStatus && !isReanalyzing && !reanalyzeProgress && (
                <div className="flex-1 overflow-y-auto mb-4">
                  <div className="text-white/60 text-sm mb-2">
                    {reanalyzeStatus.total_needs_reanalysis > 0 ? (
                      <span className="text-amber-400">
                        ⚠️ {reanalyzeStatus.total_needs_reanalysis} document(s) nécessitent une réanalyse
                      </span>
                    ) : (
                      <span className="text-emerald-400">✅ Tous les documents sont à jour</span>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {reanalyzeStatus.months?.slice(0, 12).map(month => (
                      <div 
                        key={month.month}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all"
                      >
                        <div>
                          <span className="text-white font-medium">
                            {new Date(month.month + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                          </span>
                          <div className="text-white/50 text-xs">
                            {month.with_files} doc(s) avec fichiers
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {month.needs_reanalysis > 0 && (
                            <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs">
                              {month.needs_reanalysis} à corriger
                            </span>
                          )}
                          {month.good_confidence > 0 && (
                            <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                              {month.good_confidence} OK
                            </span>
                          )}
                          <Button
                            onClick={() => startGlobalReanalysis(month.month, false)}
                            disabled={isReanalyzing || month.with_files === 0}
                            size="sm"
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Réanalyser
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Progression */}
              {isReanalyzing && (
                <div className="flex-1 flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-16 h-16 text-emerald-400 animate-spin mb-4" />
                  <p className="text-white text-lg">{reanalyzeProgress?.message || 'Réanalyse en cours...'}</p>
                  <p className="text-white/50 text-sm mt-2">
                    GPT-5.2 analyse vos documents. Cela peut prendre quelques minutes.
                  </p>
                </div>
              )}

              {/* Résultats */}
              {reanalyzeProgress && !isReanalyzing && (
                <div className="flex-1 overflow-y-auto mb-4">
                  <div className={`p-4 rounded-lg mb-4 ${
                    reanalyzeProgress.status === 'complete' ? 'bg-emerald-500/20 border border-emerald-500/50' :
                    reanalyzeProgress.status === 'error' ? 'bg-red-500/20 border border-red-500/50' :
                    'bg-white/10'
                  }`}>
                    <p className={`font-medium ${
                      reanalyzeProgress.status === 'complete' ? 'text-emerald-400' :
                      reanalyzeProgress.status === 'error' ? 'text-red-400' :
                      'text-white'
                    }`}>
                      {reanalyzeProgress.message}
                    </p>
                    {reanalyzeProgress.total !== undefined && (
                      <div className="text-white/60 text-sm mt-2 grid grid-cols-2 gap-2">
                        <span>Documents analysés: {reanalyzeProgress.analyzed || 0}</span>
                        <span>Mis à jour: {reanalyzeProgress.updated || 0}</span>
                        <span>Erreurs: {reanalyzeProgress.errors || 0}</span>
                        <span>Total: {reanalyzeProgress.total}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Détails des modifications */}
                  {reanalyzeProgress.details?.filter(d => d.status === 'updated').length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-white/70 text-sm font-medium">Documents modifiés:</h4>
                      {reanalyzeProgress.details.filter(d => d.status === 'updated').slice(0, 20).map((detail, idx) => (
                        <div key={idx} className="p-2 bg-white/5 rounded text-sm">
                          <div className="text-white">{detail.title}</div>
                          <div className="text-emerald-400 text-xs">
                            {detail.changes?.join(' | ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button
                  onClick={() => {
                    setShowReanalyzeModal(false);
                    setReanalyzeProgress(null);
                  }}
                  variant="outline"
                  className="flex-1 border-white/20 text-white"
                  disabled={isReanalyzing}
                >
                  Fermer
                </Button>
                {!isReanalyzing && !reanalyzeProgress && (
                  <>
                    <Button
                      onClick={() => startGlobalReanalysis(null, false)}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                      disabled={reanalyzeStatus?.total_needs_reanalysis === 0}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Réanalyser les problèmes ({reanalyzeStatus?.total_needs_reanalysis || 0})
                    </Button>
                    <Button
                      onClick={() => {
                        if (window.confirm('Réanalyser TOUS les documents, même ceux déjà corrects ? Cela peut prendre du temps.')) {
                          startGlobalReanalysis(null, true);
                        }
                      }}
                      variant="outline"
                      className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    >
                      Forcer tout
                    </Button>
                  </>
                )}
                {reanalyzeProgress && !isReanalyzing && (
                  <Button
                    onClick={() => {
                      setReanalyzeProgress(null);
                      loadReanalyzeStatus();
                    }}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Nouvelle réanalyse
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        </>
        )}
      </div>
    </div>
  );
}
